import React, { useState } from 'react';
import CrewTimesheetIconButton from './CrewTimesheetIconButton';

function formatDetailNumber(value) {
  if (value == null || value === '') return '—';
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return number.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function formatDetailDate(value) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return '—';
  const [year, month, day] = String(value).split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function remainingHighlight(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number === 0) return undefined;
  return number < 0 ? 'warning' : 'positive';
}

const DetailItem = ({ label, value, highlight }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
    <span
      className={`text-sm font-medium ${
        highlight === 'positive'
          ? 'text-emerald-700'
          : highlight === 'warning'
          ? 'text-amber-600'
          : 'text-gray-800'
      }`}
    >
      {value}
    </span>
  </div>
);

const PP_ACTION_BTN =
  'inline-flex items-center justify-center box-border h-9 sm:h-10 px-2 sm:px-3 text-xs sm:text-sm font-medium leading-none text-white bg-cyan-700 rounded-md hover:bg-indigo-700 transition';

function DetailLines({ title, headers, rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="pt-3 border-t border-gray-200">
      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{title}</span>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-left text-sm text-gray-800">
          {headers ? (
            <thead>
              <tr>
                {headers.map((header) => (
                  <th
                    key={header}
                    className="py-1 pr-4 text-[11px] font-semibold uppercase tracking-wide text-gray-500"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
          ) : null}
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-b border-gray-100 last:border-0">
                {row.cells.map((cell, cellIndex) => (
                  <td key={`${row.key}-${cellIndex}`} className="py-1.5 pr-4 align-top">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const TableRow = ({ data, isEven, onTrackingClick, onEnterDataClick, onCrewTimesheetClick, onDeleteEnterData }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [deletingEnterData, setDeletingEnterData] = useState(false);
  const hasEnterData = Boolean(data?.activityDetails?.hasEnterData || data?.hasEnterData);

  const handleDeleteEnterData = async () => {
    if (!hasEnterData || deletingEnterData || !onDeleteEnterData) return;
    const scopeLabel = data?.scope || "this activity";
    const confirmed = window.confirm(
      `Delete all Enter Data saved for ${scopeLabel}?\n\nThis removes only the rows saved from the Enter Data screen. Crew timesheets, production logs, and Miter timesheets stay.`
    );
    if (!confirmed) return;
    try {
      setDeletingEnterData(true);
      await onDeleteEnterData(data);
    } catch (err) {
      window.alert(err?.response?.data?.message || err?.message || "Failed to delete Enter Data");
    } finally {
      setDeletingEnterData(false);
    }
  };

  const bgColor = isEven ? 'bg-white' : 'bg-slate-300';
  const bgColorAlt = isEven ? 'bg-white' : 'bg-indigo-200';
  const bgColorAlt2 = isEven ? 'bg-white' : 'bg-orange-200';
  const bgColorAlt3 = isEven ? 'bg-white' : 'bg-cyan-200';
  const dropdownBg = isEven ? 'bg-gray-50' : 'bg-slate-100';

  return (
    <div className="w-full">
      {/* ── Main Row ── */}
      <div className={`flex w-full min-w-0 ${bgColor}`}>

        <div
          style={{ display: 'none' }}
          className={`flex-[10] basis-0 min-w-0 shrink p-1.5 sm:p-2 flex justify-center items-center ${bgColorAlt}`}
        >
          <button
            onClick={() => onEnterDataClick(data)}
            className="max-w-full px-2 sm:px-3 h-9 sm:h-10 text-xs sm:text-sm font-medium text-white text-center leading-none whitespace-normal bg-cyan-700 rounded-md hover:bg-indigo-700 transition">
            Enter Data
          </button>
        </div>

        {/* Crew Timesheet — new flow only; does not affect Enter Data / Tracking Details */}
        <div className={`flex-[14] basis-0 min-w-0 shrink p-1.5 sm:p-2 flex justify-center items-center ${bgColorAlt}`}>
          <CrewTimesheetIconButton
            variant="table"
            onClick={() => onCrewTimesheetClick?.(data)}
          />
        </div>

        <div className={`flex-[12.5] basis-0 min-w-0 shrink p-1.5 sm:p-2 flex justify-center items-center ${bgColorAlt}`}>
          <button
            onClick={() => onTrackingClick(data)}
            className={`max-w-full ${PP_ACTION_BTN} text-center whitespace-normal`}
          >
            Tracking Details
          </button>
        </div>

        {/* Activity Name — centered under header; chevron does not shift label (matches TableHeader Activity column) */}
        <div
          className={`relative flex-[20] basis-0 min-w-0 shrink p-1.5 sm:p-2 flex items-center justify-center cursor-pointer select-none hover:bg-gray-100/70 transition-colors group`}
          onClick={() => setIsExpanded((prev) => !prev)}
          title={isExpanded ? 'Collapse details' : 'Expand details'}
        >
          <span className="min-w-0 max-w-full text-xs sm:text-sm md:text-base font-medium text-center leading-snug break-words px-7">
            {data.scope}
          </span>
          <svg
            className={`pointer-events-none absolute right-1.5 sm:right-2 top-1/2 h-4 w-4 -translate-y-1/2 flex-shrink-0 text-cyan-700 transition-transform duration-200 group-hover:text-cyan-900 ${
              isExpanded ? 'rotate-180' : 'rotate-0'
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        <div className={`flex-[10] basis-0 min-w-0 shrink p-1.5 sm:p-2 flex items-center justify-center text-xs sm:text-sm md:text-base ${bgColorAlt}`}>
          <span className="min-w-0 text-center break-words">{data.hoursUsed}</span>
        </div>

        <div className={`flex-[7.5] basis-0 min-w-0 shrink p-1.5 sm:p-2 flex items-center justify-center text-xs sm:text-sm md:text-base ${bgColorAlt2}`}>
          <span className="min-w-0 text-center break-words">{data.unitsPlaced}</span>
        </div>

        <div className={`flex-[7.5] basis-0 min-w-0 shrink p-1.5 sm:p-2 flex items-center justify-center text-xs sm:text-sm md:text-base ${bgColorAlt3}`}>
          <span className="min-w-0 text-center break-words">
            {data.crewDaysUsed} / {data.estCrewDays}
          </span>
        </div>

        <div className={`flex-[10] basis-0 min-w-0 shrink p-1.5 sm:p-2 flex items-center justify-center text-xs sm:text-sm md:text-base ${bgColorAlt2}`}>
          <span className="min-w-0 text-center break-words">{data.crewSize}</span>
        </div>

        <div className={`flex-[10] basis-0 min-w-0 shrink p-1.5 sm:p-2 flex items-center justify-center text-xs sm:text-sm md:text-base ${bgColorAlt2}`}>
          <span className="min-w-0 text-center break-words">{data.productionPerDay}</span>
        </div>

        {(() => {
          const equipmentItems =
            data.rentalEquipment && data.rentalEquipment !== '--'
              ? String(data.rentalEquipment).split(' / ').map((item) => item.trim()).filter(Boolean)
              : [];
          const daysItems =
            data.estRentalDays && data.estRentalDays !== '--'
              ? String(data.estRentalDays).split(' / ').map((item) => item.trim()).filter(Boolean)
              : [];

          const maxLen = Math.max(equipmentItems.length, daysItems.length);
          const pairs = [];
          for (let i = 0; i < maxLen; i++) {
            pairs.push({ eq: equipmentItems[i] || '--', day: daysItems[i] || '--' });
          }

          const rentalCell =
            'flex-[12.5] basis-0 min-w-0 shrink p-1.5 sm:p-2 flex flex-col items-center justify-center gap-0.5 text-xs sm:text-sm';

          return (
            <>
              <div className={`${rentalCell} ${bgColorAlt2}`}>
                {maxLen === 0 ? (
                  <span className="min-w-0 text-center break-words">--</span>
                ) : (
                  pairs.map((pair, idx) => (
                    <div
                      key={`eq-${idx}`}
                      className="flex min-h-[1.5rem] w-full min-w-0 items-center justify-center text-center text-sm"
                      title={pair.eq}
                    >
                      <span className="min-w-0 truncate">{pair.eq}</span>
                    </div>
                  ))
                )}
              </div>
              <div className={`${rentalCell} ${bgColorAlt2}`}>
                {maxLen === 0 ? (
                  <span className="min-w-0 text-center break-words">--</span>
                ) : (
                  pairs.map((pair, idx) => (
                    <div
                      key={`day-${idx}`}
                      className="flex min-h-[1.5rem] w-full min-w-0 items-center justify-center text-center text-sm"
                      title={pair.day}
                    >
                      <span className="min-w-0 truncate">{pair.day}</span>
                    </div>
                  ))
                )}
              </div>
            </>
          );
        })()}
      </div>

      {/* ── Expandable Dropdown Panel ── */}
      {isExpanded && (
        <div className={`w-full border-t-2 border-cyan-700/30 ${dropdownBg} px-6 py-5 shadow-inner`}>
          {/* Header */}
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-1 h-5 rounded bg-cyan-700" />
              <span className="text-sm font-semibold text-cyan-800 tracking-wide uppercase">
                Activity Details — {data.scope}
              </span>
            </div>
            {hasEnterData ? (
              <button
                type="button"
                onClick={handleDeleteEnterData}
                disabled={deletingEnterData}
                className="shrink-0 rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
              >
                {deletingEnterData ? "Deleting..." : "Delete Enter Data"}
              </button>
            ) : null}
          </div>

          {(() => {
            const details = data.activityDetails;
            const productionLines = (details?.productionLines || []).length > 1
              ? details.productionLines.map((line, index) => ({
                key: `prod-${index}`,
                cells: [
                  line.code || '—',
                  line.description || '—',
                  line.uom || '—',
                  `${formatDetailNumber(line.installed)} / ${formatDetailNumber(line.budgetQuantity)}`,
                  line.percentInstalled || '—',
                ],
              }))
              : [];
            const inventoryLines = (details?.inventoryLines || []).map((line, index) => ({
              key: `inv-${index}`,
              cells: [
                line.code || '—',
                line.description || '—',
                line.uom || '—',
                formatDetailNumber(line.budgetQuantity),
              ],
            }));

            return (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-4 mb-4">
                  <DetailItem label="Activity Code (cost code)" value={details?.costCode || '—'} />
                  <DetailItem label="Unit of Measure" value={details?.unitOfMeasure || '—'} />
                  <DetailItem label="Budget Qty" value={formatDetailNumber(details?.budgetQuantity)} />
                  <DetailItem label="Installed Qty" value={formatDetailNumber(details?.installedQuantity)} />
                  <DetailItem
                    label="Qty Remaining"
                    value={formatDetailNumber(details?.quantityRemaining)}
                    highlight={remainingHighlight(details?.quantityRemaining)}
                  />
                  <DetailItem
                    label="% Complete"
                    value={details?.percentComplete || '—'}
                    highlight={details?.percentComplete ? 'positive' : undefined}
                  />
                  <DetailItem label="Budget Hours" value={formatDetailNumber(details?.budgetHours)} />
                  <DetailItem label="Hours Used" value={formatDetailNumber(details?.hoursUsed)} />
                  <DetailItem
                    label="Hours Remaining"
                    value={formatDetailNumber(details?.hoursRemaining)}
                    highlight={remainingHighlight(details?.hoursRemaining)}
                  />
                  <DetailItem
                    label="% Hours Used"
                    value={details?.percentHoursUsed || '—'}
                    highlight={details?.percentHoursUsed ? 'positive' : undefined}
                  />
                  <DetailItem label="First Worked" value={formatDetailDate(details?.firstWorked)} />
                  <DetailItem label="Last Worked" value={formatDetailDate(details?.lastWorked)} />
                </div>

                <DetailLines
                  title="Production Lines"
                  headers={['Code', 'Description', 'Unit', 'Installed / Budget', '% Installed']}
                  rows={productionLines}
                />
                <DetailLines
                  title="Inventory"
                  headers={['Code', 'Description', 'Unit', 'Budget Qty']}
                  rows={inventoryLines}
                />

                <div className="flex flex-col gap-1 pt-3 border-t border-gray-200">
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Field Notes</span>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {details?.latestNote || 'No field notes for this activity.'}
                  </p>
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default TableRow;
